<template>
  <div>
    <v-autocomplete
      required
      variant="outlined"
      density="compact"
      v-model="selectedCompany"
      @update:search="onSearchInput"
      @blur="companies = []"
      @click:clear="companies = []"
      v-model:search="searchStr"
      data-testid="yard-asset-company-field"
      :items="companies"
      :placeholder="t('yard.placeholders.searchByName')"
      item-value="id"
      item-title="name"
      hide-details
      return-object
      clearable
      no-filter
      ref="companySelect"
      persistent-hint
      :loading="isLoading"
      :disabled="disabled">
      <template
        v-slot:prepend-item
        v-if="!isLoading && searchStr?.length > 0 && companies.length > 0">
        <v-list-item
          data-testid="yard-use-custom-company"
          class="add-custom-company"
          role="option"
          @click="addCustomCompany">
          <v-list-item-title>
            {{ t('yard.messages.selectAsCarrier', { name: searchStr }) }}
          </v-list-item-title>
        </v-list-item>
        <v-divider v-if="!isLoading && searchStr" />
      </template>

      <template v-slot:item="{ item, props }">
        <v-list-item v-bind="props" :title="item.title" role="option" />
        <v-divider v-if="item.index < companies.length - 1" />
      </template>

      <template #no-data>
        <v-list-item
          data-testid="yard-use-custom-company"
          role="option"
          class="add-custom-company"
          v-if="!isLoading && searchStr?.length >= 1 && companies.length === 0"
          @click="addCustomCompany">
          <v-list-item-title>
            {{ t('yard.messages.selectAsCarrier', { name: searchStr }) }}
          </v-list-item-title>
        </v-list-item>
        <v-list-item v-else>{{ t('yard.messages.typeToSearchForACompany') }}</v-list-item>
      </template>
    </v-autocomplete>
  </div>
</template>

<script>
import { get } from 'lodash';
import { defineComponent, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDebounce } from '@satellite/composables/useDebounce';
import companyService from '@satellite/services/company-service';

export default defineComponent({
  emits: ['update:modelValue'],
  props: {
    modelValue: { type: Object, default: null },
    disabled: { type: Boolean, default: false }
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const companies = ref([]);
    const count = ref(0);
    const total = ref(0);
    const page = ref(1);
    const limit = ref(30);
    const searchStr = ref(null);

    const selectedCompany = ref(props.modelValue ?? null);
    const isLoading = ref(false);
    const companySelect = ref(null);

    const getData = async () => {
      isLoading.value = true;

      if (!searchStr.value) {
        isLoading.value = false;
        companies.value = [];
        return;
      }

      const offset = (page.value - 1) * limit.value;
      const payload = {
        search: searchStr.value,
        pagination: { limit: limit.value, offset }
      };
      try {
        const response = await companyService.autocomplete(payload);

        total.value = get(response, 'total', 0);
        count.value = get(response, 'count', 0);

        const items = get(response, 'items', []);

        companies.value = items.map((item, index) => ({
          id: item.id,
          name: item.name,
          scac: item.scac,
          index
        }));
      } catch (err) {
        console.error('Error fetching companies:', err);
      } finally {
        isLoading.value = false;
      }
    };

    const { trigger: triggerGetData } = useDebounce(getData, 300);

    const onSearchInput = inputVal => {
      searchStr.value = inputVal;
      if (inputVal?.length >= 1) {
        triggerGetData();
      } else {
        companies.value = [];
      }
    };

    const addCustomCompany = () => {
      if (searchStr.value) {
        selectedCompany.value = {
          id: null,
          name: searchStr.value
        };

        // Close the dropdown after selecting the custom company
        companySelect.value?.blur?.();

        companies.value = [];
      }
    };

    // Keep internal state in sync with parent and emit changes
    watch(
      () => props.modelValue,
      val => {
        selectedCompany.value = val;
      }
    );
    watch(selectedCompany, val => {
      emit('update:modelValue', val);
    });

    return {
      t,
      companies,
      count,
      total,
      page,
      limit,
      searchStr,
      selectedCompany,
      isLoading,
      companySelect,
      addCustomCompany,
      getData,
      onSearchInput
    };
  }
});
</script>
<style lang="scss">
.add-custom-company {
  min-height: 60px !important;
  position: sticky;
  top: -8px;
  z-index: 2;
  background: $white;
}
</style>
